import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React from 'react';
import type { VacationRequest } from '../../../models/vacation.types';
import { resolveRequestOutcome } from '../../../utils/resolveRequestOutcome';
import { formatDateSafe } from '@/utils/dateUtils';
import { VacationEventHistory } from '../../VacationEventHistory/VacationEventHistory';

interface HistoryEntryProps {
  request: VacationRequest;
}

export function HistoryEntry({ request }: HistoryEntryProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const outcome = resolveRequestOutcome(request);

  if (!outcome) return null;

  const totalDays = request.totalDays || 0;

  return (
    <div className="border rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors',
          'flex items-center justify-between gap-3'
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={clsx(
                'inline-block px-2.5 py-0.5 rounded text-xs font-semibold',
                outcome.tone === 'success' && 'bg-emerald-100 text-emerald-800',
                outcome.tone === 'danger' && 'bg-red-100 text-red-800',
                outcome.tone === 'warning' && 'bg-amber-100 text-amber-800',
                outcome.tone === 'neutral' && 'bg-gray-100 text-gray-800'
              )}
            >
              {outcome.label}
            </span>

            <span className="text-sm text-gray-700">
              {totalDays} {totalDays === 1 ? 'dia' : 'dias'}
            </span>

            {outcome.decisionDate && (
              <span className="text-sm text-gray-600">{formatDateSafe(outcome.decisionDate)}</span>
            )}
          </div>

          {request.segments.length > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              {request.segments.map((seg, i) => (
                <span key={i}>
                  {formatDateSafe(seg.startDate)} a {formatDateSafe(seg.endDate)}
                  {i < request.segments.length - 1 && ' · '}
                </span>
              ))}
            </div>
          )}
        </div>

        <ChevronDownIcon
          className={clsx('h-5 w-5 text-gray-400 transition-transform', isExpanded && 'rotate-180')}
        />
      </button>

      {isExpanded && (
        <div className="border-t px-4 py-3 bg-gray-50">
          <VacationEventHistory
            requestId={request.id}
            request={request}
            className="mt-2"
          />
        </div>
      )}
    </div>
  );
}
